import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import type { WaterTestimonial, WaterTestimonialCreateOrUpdate } from '@models/water-testimonial';
import { TranslatePipe } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';

export interface WaterTestimonialDialogData {
  testimonial: WaterTestimonial | null;
}

interface TestimonialForm {
  siteId: FormControl<number | null>;
  title: FormControl<string>;
  testimonial: FormControl<string>;
  score: FormControl<number>;
}

const DEFAULT_SCORE = 8;

@Component({
  selector: 'app-water-testimonial-dialog',
  templateUrl: './water-testimonial-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSliderModule,
    MatButtonModule,
    TranslatePipe,
  ],
})
export class WaterTestimonialDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<WaterTestimonialDialogComponent, WaterTestimonialCreateOrUpdate>);
  private readonly data = inject<WaterTestimonialDialogData>(MAT_DIALOG_DATA);
  readonly codeListsService = inject(CodeListsService);

  readonly isEdit = this.data.testimonial !== null;

  readonly form = new FormGroup<TestimonialForm>({
    siteId: new FormControl(this.data.testimonial?.siteId ?? null),
    title: new FormControl(this.data.testimonial?.title ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    testimonial: new FormControl(this.data.testimonial?.testimonial ?? '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    score: new FormControl(this.data.testimonial?.score ?? DEFAULT_SCORE, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), Validators.max(10)],
    }),
  });

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.dialogRef.close({
      siteId: raw.siteId,
      title: raw.title.trim(),
      testimonial: raw.testimonial.trim(),
      score: raw.score,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
